import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

interface PaymentMethod {
  type: 'stripe' | 'pay_later' | 'venmo';
  enabled: boolean;
  label: string;
  instructions?: string;
  venmo_handle?: string;
}

interface PaymentMethodsTabProps {
  paymentMethods: PaymentMethod[];
  onUpdate: (paymentMethods: PaymentMethod[]) => void;
}

export default function PaymentMethodsTab({ paymentMethods = [], onUpdate }: PaymentMethodsTabProps) {
  // Ensure all payment methods exist with defaults
  const ensureDefaults = (methods: PaymentMethod[]): PaymentMethod[] => {
    const defaults: PaymentMethod[] = [
      { type: 'stripe', enabled: true, label: 'Credit Card (Stripe)' },
      { type: 'pay_later', enabled: false, label: 'Pay Later / Venmo', instructions: '' },
    ];

    const methodsMap = new Map(methods.map(m => [m.type, m]));
    
    return defaults.map(def => {
      const existing = methodsMap.get(def.type);
      return existing ? { ...def, ...existing } : def;
    });
  };

  const currentMethods = ensureDefaults(paymentMethods);

  const updateMethod = (type: string, updates: Partial<PaymentMethod>) => {
    const updated = currentMethods.map(m => 
      m.type === type ? { ...m, ...updates } : m
    );
    onUpdate(updated);
  };

  const getMethod = (type: string) => currentMethods.find(m => m.type === type);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Payment Methods</h3>
        <p className="text-sm text-muted-foreground">
          Select which payment methods registrants can use for this event.
        </p>
      </div>

      {/* Stripe/Credit Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Checkbox
              id="stripe"
              checked={getMethod('stripe')?.enabled || false}
              onCheckedChange={(checked) => updateMethod('stripe', { enabled: !!checked })}
            />
            <div className="flex-1">
              <CardTitle className="text-base">Credit Card (Stripe)</CardTitle>
              <CardDescription>
                Accept credit and debit card payments via Stripe
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Pay Later / Venmo */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Checkbox
              id="pay_later"
              checked={getMethod('pay_later')?.enabled || false}
              onCheckedChange={(checked) => updateMethod('pay_later', { enabled: !!checked })}
            />
            <div className="flex-1">
              <CardTitle className="text-base">Pay Later / Venmo</CardTitle>
              <CardDescription>
                Allow registrants to complete registration and pay later via check, cash, or Venmo on their dashboard
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        {getMethod('pay_later')?.enabled && (
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="pay_later_instructions">Instructions for Registrants</Label>
              <Textarea
                id="pay_later_instructions"
                value={getMethod('pay_later')?.instructions || ''}
                onChange={(e) => updateMethod('pay_later', { instructions: e.target.value })}
                placeholder="Example: Payment can be made later via your dashboard. You can pay by check, cash, or Venmo."
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                These instructions will be shown to registrants who select "Pay Later / Venmo"
              </p>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
